'use strict';

import * as util from '../util/Common.js';
import {Point2D} from '../util/Geometry.js';
import * as domUtil from '../util/Dom.js';
import {UiComponent} from './UiComponent.js';
import {RelativeContainer} from './RelativeContainer.js';

const privateMap = util.createPrivateMapper();

export class SlideBar extends UiComponent {
  constructor(domEl, argOptions){
    super(domEl);

    const pv = privateMap(this);
    const _self = this;
    const options = argOptions ? argOptions : {};

    let cursor = domUtil.createElement('div');
    const size = new Point2D();

    const minCursorSize = typeof(options.minCursorSize) == 'number' ? options.minCursorSize : 5;
    pv.direction = options.direction ? options.direction : 'H';
    const actionInterceptor = options.actionInterceptor;
    const slideArea = options.slideAreaEl ? options.slideAreaEl : domEl;

    this.slideArea = slideArea;
    pv.relativeContainer = new RelativeContainer(slideArea);

    let min = options.min ? options.min : 0;
    let max = options.max ? options.max : 100;
    let cursorRange = options.cursorRange ? options.cursorRange : 0;

    let cursorMargin = 0;
    let disabled = false;

    let position = 0;
    let cursorSize = 1;
    let cursorRatio = 1;
    let ratio = 1;
    let boundDiff;
    let actionLocate;

    const calculate = () => {
      let cs;
      if (max <= min || max - min <= cursorRange) {
        this.visible = false;
        disabled = true;
        return;
      }
      disabled = false;
      this.visible = true;
      if (pv.direction == 'H' || pv.direction == 'RL') {
        ratio = size.x / (max - min);
        cursorSize = Math.max(ratio * cursorRange, minCursorSize);
        cursorRatio = (size.x / (max - cursorRange - min)) * (1 - cursorSize / size.x);
        cs = new Point2D(cursorSize, size.y);
      } else {
        ratio = size.y / (max - min);
        cursorSize = Math.max(ratio * cursorRange, minCursorSize);
        cursorRatio = (size.y / (max - cursorRange - min)) * (1 - cursorSize / size.y);
        cs = new Point2D(size.x, cursorSize);
      }
      if (cursorRange * ratio < minCursorSize) {
        cursorMargin = minCursorSize / 2;
      } else {
        cursorMargin = cursorSize / 2;
      }
      domUtil.setSize(cursor, cs.sub(boundDiff));
      locate(position, true);
    };
    pv.applySize = function(){
      const sz = domUtil.getSize(slideArea);
      size.x = sz.x;
      size.y = sz.y;
      boundDiff = domUtil.getOffsetSize(cursor).sub(domUtil.getSize(cursor));
      if (pv.direction == 'H' || pv.direction == 'RL') {
        domUtil.setHeight(cursor, sz.y);
      } else {
        domUtil.setWidth(cursor, sz.x);
      }
      calculate();
      return size;
    };
    const locate = function(p, force, noEvent){
      let np;
      np = Math.max(min, Math.min(max - cursorRange, p));
      if (disabled) {
        return;
      }
      if (isNaN(np)) {
        return;
      }
      if (!force && position == np) {
        return;
      }
      position = np;
      const cusrsorPos = new Point2D();
      if (pv.direction == 'H') {
        cusrsorPos.x = (position - min) * cursorRatio;
      } else if (pv.direction == 'RL') {
        cusrsorPos.x = (max - position - cursorRange) * cursorRatio;
      } else if (pv.direction == 'V') {
        cusrsorPos.y = (position - min) * cursorRatio;
      } else if (pv.direction == 'BT') {
        cusrsorPos.y = (max - position - cursorRange) * cursorRatio;
      }
      domUtil.setPosition(cursor, cusrsorPos);
      if (!noEvent) {
        domUtil.dispatchEvent(domEl, 'ui.change', {
          detail: np
        });
      }
    };
    actionLocate = actionInterceptor ? function(p){
      let np;
      if (disabled) {
        return;
      }
      np = Math.max(min, Math.min(max - cursorRange, p));
      actionInterceptor(np);
    } : locate;

    domUtil.asAbsolute(cursor);
    domUtil.noBoundary(cursor);
    if (options.cursorEl) {
      cursor.appendChild(options.cursorEl);
    }
    pv.relativeContainer.domEl.appendChild(cursor);

    const dragHandler = (function(){
      let basePosition = position;
      let dragged = false;
      let base = new Point2D();
      let lastPt = new Point2D();
      const mousemoveHandler = function(ev, touch){
        const pt = new Point2D(ev.clientX, ev.clientY);
        const delta = (pt).sub(base);
        let p;
        lastPt = pt;
        if (pv.direction == 'H') {
          if (delta.x == 0) {
            return;
          }
          p = basePosition + delta.x / cursorRatio;
        } else if (pv.direction == 'RL') {
          if (delta.x == 0) {
            return;
          }
          p = basePosition - delta.x / cursorRatio;
        } else if (pv.direction == 'V') {
          if (delta.y == 0) {
            return;
          }
          p = basePosition + delta.y / cursorRatio;
        } else if (pv.direction == 'BT') {
          if (delta.y == 0) {
            return;
          }
          p = basePosition - delta.y / cursorRatio;
        }
        dragged = true;
        actionLocate(p);
      };

      const mouseupHandler = function(ev){
        domUtil.removeEventListener(window, 'mousemove', mousemoveHandler);
        domUtil.removeEventListener(window, 'mouseup', mouseupHandler);
        if (!dragged && lastPt) {
          let px = domUtil.getPixel(slideArea, lastPt).sub(1, 1);
          let p;
          if (pv.direction == 'H') {
            p = (px.x - cursorMargin) / cursorRatio + min;
          } else if (pv.direction == 'RL') {
            p = max - (px.x + cursorMargin) / cursorRatio;
          } else if (pv.direction == 'V') {
            p = (px.y - cursorMargin) / cursorRatio + min;
          } else if (pv.direction == 'BT') {
            p = max - (px.y + cursorMargin) / cursorRatio;
          }
          actionLocate(p);
        }
        domUtil.dispatchEvent(domEl, 'ui.dragend');
      }

      return function(ev){
        const pt = new Point2D(ev.clientX, ev.clientY);
        ev.preventDefault();
        dragged = false;
        if (disabled) {
          return false;
        }
        basePosition = position;
        base = pt;
        lastPt = pt;
        domUtil.dispatchEvent(domEl, 'ui.dragstart');
        domUtil.addEventListener(window, 'mousemove', mousemoveHandler);
        domUtil.addEventListener(window, 'mouseup', mouseupHandler);
      }

    })();
    domUtil.addEventListener(pv.relativeContainer.domEl, 'mousedown', dragHandler);

    this.setPosition = function(p, force, noEvent){
      locate(p, force, noEvent);
    };
    this.getPosition = function(){
      return position;
    };
    this.setTerms = function(t){
      if (!t) {
        return;
      }
      if (t.min != undefined) {
        min = t.min;
      }
      if (t.max != undefined) {
        max = t.max;
      }
      if (t.cursorRange != undefined) {
        cursorRange = t.cursorRange;
      }
      calculate();
    };
    this.setRange = function(argMin, argMax){
      min = argMin;
      max = argMax;
      calculate();
    };
    this.setCursorRange = function(v){
      cursorRange = v;
      calculate();
    };
    this.isDisabled = function(){
      return disabled;
    };

    this.applySize();
  }

  set direction(direction){
    privateMap(this).direction = direction;
    this.applySize();
  }

  applySize(){
    privateMap(this).applySize();
  };
};
